import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  loadTokens,
  signOut as triggerSignOut,
  tokensExpired,
  userFromTokens,
  type AuthUser,
} from "./auth";

type Status = "loading" | "anonymous" | "authenticated";

interface AuthContextValue {
  status: Status;
  user: AuthUser | null;
  /** Navigate to the sign-in form. Preserves a return path via ?next=. */
  goToSignIn: (postLoginPath?: string) => void;
  signOut: () => void;
  /** Force the context to re-read tokens (e.g. after sign-in success). */
  refresh: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [status, setStatus] = useState<Status>("loading");
  const navigate = useNavigate();

  const refresh = useCallback(() => {
    const tokens = loadTokens();
    if (!tokens || tokensExpired(tokens)) {
      setUser(null);
      setStatus("anonymous");
      return;
    }
    const u = userFromTokens(tokens);
    if (!u) {
      setUser(null);
      setStatus("anonymous");
      return;
    }
    setUser(u);
    setStatus("authenticated");
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      goToSignIn: (postLoginPath?: string) => {
        const qs = postLoginPath ? `?next=${encodeURIComponent(postLoginPath)}` : "";
        navigate(`/signin${qs}`);
      },
      signOut: () => {
        void triggerSignOut().finally(() => {
          setUser(null);
          setStatus("anonymous");
          navigate("/");
        });
      },
      refresh,
    }),
    [status, user, refresh, navigate],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
